import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { Button, Modal, Row, Col, Form, OverlayTrigger } from "react-bootstrap";
import { MdEdit, MdAddCircle, MdFilterList, MdDelete } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Tooltip } from '@mui/material';
import loading from "../../assets/images/common/loading.gif";
import AppDataGrid from "../../components/common/AppDataGrid";

const Level = () => {
    const [levels, setLevels] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [showFilterModal, setShowFilterModal] = useState(false);
    const handleCloseFilterModal = () => setShowFilterModal(false);
    const handleShowFilterModal = () => setShowFilterModal(true);
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    const navigate = useNavigate();
    const userData = sessionStorage.getItem('user');
    const userObj = JSON.parse(userData);
    const [form, setForm] = useState({
        description: "",
        level_id: "",
        school_id: "",
        action: "FILTER"
    });

    useEffect(() => {
        setIsLoading(true);
        fetchData().finally(() => setIsLoading(false));
    }, []);

    const handleEditClick = (level_id) => {
        const employeemasterToEdit = levels.find(level => level.level_id === level_id);

        if (employeemasterToEdit) {
            navigate("/addlevel", { state: { levelsData: employeemasterToEdit } });
        } else {
            console.error(`Subject with ID ${level_id} not found.`);
        }
    };

    const fetchData = async () => {
        try {
            const response = await axios.post(baseUrl + "/level/", {
                action: "READ",
                school_id: userObj.school_id
            });

            setLevels(response.data);
        } catch (error) {
            console.log("Error fetching data:", error);
        }
    };

    const handleDeleteClick = async (level_id) => {
        const confirmDelete = window.confirm('Are you sure you want to change the status?');

        if (!confirmDelete) {
            return;
        }
        const requestBody = {
            level_id: level_id,
            action: "DELETE"
        };

        try {
            const response = await axios.post(baseUrl + '/level/', requestBody, {
                headers: {
                    'Content-Type': 'application/json'
                },
            });
            if (response.status < 200 || response.status >= 300) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            toast.success("Record Set To InActive ");
            fetchData();
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const searchableColumns = [
        (row) => (row.description),
        (row) => (row.is_active),
    ];

    const normalizedQuery = (searchQuery || '').toLowerCase().replace(/[-\s]+/g, '');

    // ✅ safer search: handle null/undefined values
    const filteredRecords = (levels || []).filter((item) =>
        searchableColumns.some((selector) => {
            const value = selector(item);
            const stringValue = String(value || '').toLowerCase().replace(/[-\s]+/g, '');
            return stringValue.includes(normalizedQuery);
        })
    );

    const baseColumns = [
        {
            field: "description",
            headerName: "Level Name",
            flex: 1,
            renderCell: (params) => (
                <Tooltip title={params.value}>
                    <span className="cell-wrap">{params.value}</span>
                </Tooltip>
            ),
        },
        {
            field: "is_active",
            headerName: "Status",
            flex: 1,
            renderCell: (params) => (
                <Tooltip title={params.value}>
                    <span className="cell-wrap">{params.value}</span>
                </Tooltip>
            ),
        },
    ];

    const columns = [
        ...baseColumns,
        {
            field: "actions",
            headerName: "Actions",
            flex: 1,
            sortable: false,
            renderCell: (params) =>
                params.row.is_active === "No records found" ? null : (
                    <div className="tableActions">
                        <Tooltip title="Edit" arrow>
                            <span
                                className="commonActionIcons"
                                onClick={() => handleEditClick(params.row.level_id)}
                            >
                                <MdEdit />
                            </span>
                        </Tooltip>
                        <Tooltip title="Delete" arrow>
                            <span
                                className="commonActionIcons"
                                onClick={() => handleDeleteClick(params.row.level_id)}
                            >
                                <MdDelete />
                            </span>
                        </Tooltip>
                    </div>
                ),
        },
    ];

    const handleInputChange = (e) => {
        const { id, value } = e.target;

        setForm((prevForm) => ({
            ...prevForm,
            [id]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const formData = {
            description: form.description,
            school_id: userObj.school_id,
            action: 'FILTER',
        };

        try {
            const response = await axios.post(baseUrl + "/level/", formData, {
                headers: {
                    "Content-Type": "application/json",
                },
            });

            const data = response.data;

            if (
                data &&
                typeof data === "object" &&
                data.message === "No data found for action: FILTER"
            ) {
                setLevels([]);
                setShowFilterModal(false);

            } else if (Array.isArray(data)) {
                setLevels(data);
                setShowFilterModal(false);
            } else {
                setLevels([]);
                setShowFilterModal(false);
            }

        } catch (error) {
            console.error("Error fetching filtered data:", error);
            setLevels([]);
            setShowFilterModal(false);
        }
    };

    const handleReset = async (e) => {
        e.preventDefault();
        setForm({
            description: "",
        });
        setShowFilterModal(true);
        fetchData();
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    return (
        <div className="pageBody">
            <ToastContainer />
            <div className="commonDataTableHead">
                <div className="d-flex justify-content-between align-items-center w-100">
                    <div className="d-flex align-items-center" style={{ gap: "10px" }}>
                        <h6 className="commonTableTitle">Level Master</h6>
                    </div>
                    <div className="">
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            className="searchInput"
                            onChange={handleSearchChange}
                        />
                    </div>
                    <div className="d-flex align-items-center" style={{ gap: 6 }}>
                        <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Filter</Tooltip>}>
                            <Button className="secondaryBtn" variant="secondary" onClick={handleShowFilterModal}>
                                <MdFilterList />
                            </Button>
                        </OverlayTrigger>
                        {
                            <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-top">Add</Tooltip>}>
                                <Button className="primaryBtn" variant="primary" onClick={() => navigate("/addlevel")}>
                                    <MdAddCircle />
                                </Button>
                            </OverlayTrigger>
                        }
                    </div>
                </div>
            </div>
            <div className="commonTable height100">
                <div className="tableBody">
                    {isLoading ? (
                        <div className="loadingContainer">
                            <img src={loading} alt="Loading..." className="loadingGif" />
                        </div>
                    ) : (
                        <AppDataGrid
                            // ✅ let DataGrid handle "No rows" by itself
                            rows={filteredRecords}
                            columns={columns}
                            getRowId={(row) => row.level_id}
                            showToolbar={false}
                            pagination
                            pageSizeOptions={[10, 20, 50]}
                            density="compact"
                            maxHeight={550}
                        />
                    )}
                </div>
            </div>

            <Modal show={showFilterModal} onHide={handleCloseFilterModal} className="commonFilterModal">
                <Modal.Header closeButton className="modalHeaderFixed">
                    <Modal.Title>Filter</Modal.Title>
                </Modal.Header>
                <Modal.Body className="modalBodyScrollable">
                    <Row>
                        <Col xs={12}>
                            <div className="commonInput">
                                <Form.Group controlId="levelName">
                                    <Form.Label>Level Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        id="description"
                                        value={form.description}
                                        placeholder="Enter Level Name"
                                        onChange={handleInputChange}
                                        onKeyDown={(e) => {
                                            if (e.key === ' ' && e.target.selectionStart === 0) {
                                                e.preventDefault();
                                            }
                                        }}
                                        maxLength={30}
                                    />
                                </Form.Group>
                            </div>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer className="modalFooterFixed">
                    <Button variant="secondary" className="btn-info clearBtn me-2" onClick={handleReset}>
                        Reset
                    </Button>
                    <div>
                        <Button variant="secondary" className="btn-danger secondaryBtn me-2" onClick={() => { handleCloseFilterModal(); }}>
                            Cancel
                        </Button>

                        <Button variant="primary" className="btn-success primaryBtn" onClick={handleSubmit}>
                            Search
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default Level;
